import * as yup from 'yup';

import { api, isDefined, isTruthy } from 'common';

import { yupEnum, yupUuid } from '../../../utils/form';

export enum DialogTrigger {
  Form = 'FORM',
  FormOptions = 'FORM_OPTIONS',
  Signature = 'SIGNATURE',
}
export interface DialogProps {
  trigger?: DialogTrigger;
  onContinue?: () => void;
  onBack?: () => void;
}

export type Command = api.DocumentTemplate.Command;
type FieldConfigWithConditions =
  api.PrevisitFormTemplateFieldConfig.PrevisitFormTemplateFieldConfigWithConditions;
export type Template = api.DocumentTemplate.CreateValues & {
  id?: string;
  formTemplate: { linked: boolean; id?: string; name?: string };
  formFields?: FieldConfigWithConditions[];
  documentSignerConfig?: FieldConfigWithConditions;
  documentTemplateConfigurations?: DocumentTemplateConfiguration[];
};

export type DocumentTemplateConfiguration = Omit<
  api.Orchestrator.DocumentTemplateConfiguration.CreateValues,
  'deleted'
> & { id?: string; __key?: string };

export const Category = api.DocumentTemplate.Category;
export const DataSource = api.DocumentTemplate.DataSource;
export const Font = api.DocumentTemplate.Font;
export const PointReference = api.DocumentTemplate.PointReference;
export const Primitive = api.PrevisitFormTemplateFieldConfig.Primitive;
export const CloseNoteAction = api.Orchestrator.DocumentTemplateConfiguration.CloseNoteAction;

export const DataSourceDisplayNames: Record<api.DocumentTemplate.DataSource, string> = {
  CROSSMARK: 'Crossmark',
  DATE_SIGNED: 'Date signed',
  EHR_SCHEDULED: 'EHR scheduled',
  EMERGENCY_CONTACT_NAME: 'Emergency contact name',
  EMERGENCY_CONTACT_RELATIONSHIP: 'Emergency contact relationship',
  EMERGENCY_CONTACT_PHONE: 'Emergency contact phone',
  GUARDIAN_RELATIONSHIP: 'Guardian relationship',
  MRN: 'MRN',
  NONE: 'None',
  SIGNATURE: 'Signature',
  GUARANTOR_AGE: 'Guarantor age',
  GUARANTOR_DOB: 'Guarantor date of birth',
  GUARANTOR_HOME_ADDRESS: 'Guarantor home address',
  GUARANTOR_HOME_FULL_ADDRESS: 'Guarantor full home address',
  GUARANTOR_HOME_CITY_STATE_ZIP: 'Guarantor home city, state, ZIP',
  GUARANTOR_HOME_PHONE_NUMBER: 'Guarantor home phone number',
  GUARANTOR_NAME: 'Guarantor name',
  GUARANTOR_RELATIONSHIP: 'Guarantor relationship',
  GUARANTOR_RELATIONSHIP_TO_PATIENT: 'Guarantor relationship to patient',
  GUARANTOR_SEX: 'Guarantor sex',
  GUARANTOR_SSN: 'Guarantor SSN',
  PATIENT_AGE: 'Patient age',
  NEWBORN_AGE: 'Newborn age',
  PATIENT_BIRTH_GESTATIONAL_AGE: 'Patient birth gestational age',
  PATIENT_BIRTH_WEIGHT_GRAMS: 'Patient birth weight grams',
  PATIENT_CARE_PROVIDER_ADDRESS: 'Patient care provider address',
  PATIENT_CARE_PROVIDER_NAME: 'Patient care provider name',
  PATIENT_DOB: 'Patient DOB',
  PATIENT_EMAIL: 'Patient email',
  PATIENT_EMPLOYER: 'Patient employer',
  PATIENT_PREFERRED_LANGUAGE: 'Patient preferred language',
  PATIENT_HOME_ADDRESS: 'Patient home address',
  PATIENT_HOME_FULL_ADDRESS: 'Patient full home address',
  PATIENT_HOME_CITY_STATE_ZIP: 'Patient home city, state, ZIP',
  PATIENT_HOME_PHONE_NUMBER: 'Patient home phone number',
  PATIENT_INITIALS: 'Patient initials',
  PATIENT_IP: 'Patient IP',
  PATIENT_MOBILE_PHONE_NUMBER: 'Patient mobile phone number',
  PATIENT_MOTHER_NAME: 'Patient mother name',
  PATIENT_MRN: 'Patient MRN',
  PATIENT_ATHENA_IDX_MRN: 'Patient Athena IDX MRN',
  PATIENT_NAME: 'Patient name',
  PATIENT_RACE: 'Patient race',
  PATIENT_SEX: 'Patient sex',
  PATIENT_SSN: 'Patient SSN',
  PATIENT_PHARMACY_NAME_WITH_ADDRESS: 'Patient pharmacy name with address',
  PATIENT_PHARMACY_PHONE: 'Patient pharmacy phone',
  PRIMARY_INSURANCE_GROUP_NUMBER: 'Primary insurance group number',
  PRIMARY_INSURANCE_INSURANCE_NAME: 'Primary insurance insurance name',
  PRIMARY_INSURANCE_POLICY_HOLDER_AGE: 'Primary insurance policy holder age',
  PRIMARY_INSURANCE_POLICY_HOLDER_DOB: 'Primary insurance policy holder DOB',
  PRIMARY_INSURANCE_POLICY_HOLDER_HOME_ADDRESS: 'Primary insurance policy holder home address',
  PRIMARY_INSURANCE_POLICY_HOLDER_HOME_CITY_STATE_ZIP:
    'Primary insurance policy holder home city, state, ZIP',
  PRIMARY_INSURANCE_POLICY_HOLDER_HOME_PHONE_NUMBER:
    'Primary insurance policy holder home phone number',
  PRIMARY_INSURANCE_POLICY_HOLDER_NAME: 'Primary insurance policy holder name',
  PRIMARY_INSURANCE_POLICY_HOLDER_RELATIONSHIP_TO_PATIENT:
    'Primary insurance policy holder relationship to patient',
  PRIMARY_INSURANCE_POLICY_HOLDER_SEX: 'Primary insurance policy holder sex',
  PRIMARY_INSURANCE_POLICY_NUMBER: 'Primary insurance policy number',

  SECONDARY_INSURANCE_GROUP_NUMBER: 'Secondary insurance group number',
  SECONDARY_INSURANCE_INSURANCE_NAME: 'Secondary insurance insurance name',
  SECONDARY_INSURANCE_POLICY_HOLDER_AGE: 'Secondary insurance policy holder age',
  SECONDARY_INSURANCE_POLICY_HOLDER_DOB: 'Secondary insurance policy holder DOB',
  SECONDARY_INSURANCE_POLICY_HOLDER_HOME_ADDRESS: 'Secondary insurance policy holder home address',
  SECONDARY_INSURANCE_POLICY_HOLDER_HOME_CITY_STATE_ZIP:
    'SECONDARY INSURANCE POLICY HOLDER HOME CITY STATE ZIP',
  SECONDARY_INSURANCE_POLICY_HOLDER_HOME_PHONE_NUMBER:
    'Secondary insurance policy holder home phone number',
  SECONDARY_INSURANCE_POLICY_HOLDER_NAME: 'Secondary insurance policy holder name',
  SECONDARY_INSURANCE_POLICY_HOLDER_RELATIONSHIP_TO_PATIENT:
    'Secondary insurance policy holder relationship to patient',
  SECONDARY_INSURANCE_POLICY_HOLDER_SEX: 'Secondary insurance policy holder sex',
  SECONDARY_INSURANCE_POLICY_NUMBER: 'Secondary insurance policy number',
  VISIT_ADMIT_DATE: 'Visit admit date',
  VISIT_ADMIT_DATETIME: 'Visit admit datetime',
  VISIT_ADMITTING_PHYSICIAN_NAME: 'Visit admitting physician name',
  VISIT_ATTENDING_PHYSICIAN_NAME: 'Visit attending physician name',
  VISIT_BUILDING_LOCATION: 'Visit building location',
  VISIT_EHR_ID: 'Visit EHR ID',
  VISIT_EHR_TYPE: 'Visit EHR type',
  VISIT_MEDICAL_SERVICE: 'Visit medical service',
  VISIT_REASON: 'Visit reason',
};

function getCommandSchema(options: { pageCount?: number }): yup.SchemaOf<Command> {
  const { pageCount } = options;
  const page = yup.number().integer('Must be a whole number').min(0, 'Must be >= 0');
  const pageIndex = isDefined(pageCount)
    ? page
        .max(
          pageCount - 1,
          `Pages must be between 0 and ${pageCount - 1}, since this document only has ${pageCount} ${pageCount === 1 ? 'page' : 'pages'}`
        )
        .required()
    : page.required();
  return yup.object().shape({
    dataSource: yup.string().required(),
    font: yupEnum(Font).required(),
    fontSize: yup.number().min(0, 'Must be >= 0').required(),
    fontColorR: yup.number().min(0).max(255).required(),
    fontColorG: yup.number().min(0).max(255).required(),
    fontColorB: yup.number().min(0).max(255).required(),
    pageIndex,
    pointX: yup.number().min(0, 'Must be >= 0').required(),
    pointY: yup.number().min(0, 'Must be >= 0').required(),
    pointRef: yupEnum(PointReference).required(),
    lineWidth: yup.number().min(1, 'Must be >= 1').optional(),
    previsitFormFieldId: yup.string().optional(),
    mappedValue: yup.mixed().oneOf([DataSource.Crossmark, DataSource.None]).optional(),
    value: yup.string().optional(),
  });
}

const DOCUMENT_TEMPLATE_CONFIGURATION_SCHEMA: yup.SchemaOf<DocumentTemplateConfiguration> =
  yup.object({
    __key: yupUuid(),
    id: yupUuid(),
    documentTemplateId: yupUuid().required(),
    ehrInstanceId: yupUuid().nullable().required('Required'),
    title: yup.string().nullable(),
    type: yup.string().required('Required'),
    action: yupEnum([...Object.values(CloseNoteAction), null]).nullable(),
    shouldOverrideOldMappings: yup.boolean(),
    markComplete: yup.boolean(),
  });

export function getSchema(options: { pageCount?: number }): yup.SchemaOf<Template> {
  const { pageCount } = options;
  // @ts-expect-error: yup mixed typings only allow objects.
  return yup.object().shape({
    name: yup.string().required(),
    category: yupEnum(Category).required(),
    gsPath: yup.string().required('Required'),
    commands: yup.array(getCommandSchema({ pageCount }).required()).required(),
    formTemplate: yup
      .object({
        linked: yup.boolean().required(),
        id: yup.string().optional(),
        name: yup.string().optional(),
      })
      .required(),
    deleted: yup.date().nullable(),
    internalName: yup.string().optional().nullable(),
    fullReviewRequired: yup.boolean().optional(),
    viewRequired: yup.boolean().optional(),
    documentTemplateConfigurations: yup.array(DOCUMENT_TEMPLATE_CONFIGURATION_SCHEMA).optional(),
    organizationId: yupUuid().required(),
  });
}

export function cleanDocumentTemplateCommand(command: Command): api.DocumentTemplate.Command {
  return {
    ...command,
    mappedValue: isTruthy(command.mappedValue) ? command.mappedValue : undefined,
  };
}

export function cleanDocumentTemplateValues(template: Template): api.DocumentTemplate.CreateValues {
  return {
    name: template.name,
    category: template.category,
    commands: template.commands.map(cleanDocumentTemplateCommand),
    gsPath: template.gsPath,
    internalName: template.internalName || null,
    organizationId: template.organizationId || null,
    fullReviewRequired: template.fullReviewRequired,
    viewRequired: template.viewRequired,
  };
}

export function generateDisplayName(name: string) {
  return ['Document', name.trim()].join(' ');
}

export function generateName(displayName: string) {
  return [...displayName.split(' '), getRandomId()].join('-').toLowerCase().replace(/\s/g, '-');
}

function getRandomId(length: number = 5): string {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const charactersLength = characters.length;
  let result = '';
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
}
